'use client';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/components/language-provider';
import { CheckCircle2, Quote, ArrowRight } from 'lucide-react';

// 表1 痛点—影响对照（6 行）
const t1Rows = [1, 2, 3, 4, 5, 6];
// 表2 痛点—方案对应（6 行）
const t2Rows = [1, 2, 3, 4, 5, 6];
// 表3 量化指标：[功能维度, 指标, 国际常见, RobotSole, 功能维度rowSpan(0=不渲染)]
const t3Rows: [string, string, string, string, number][] = [
  ['bio.t3.r1c1', 'bio.t3.r1c2', 'bio.t3.r1c3', 'bio.t3.r1c4', 3],
  ['bio.t3.r2c1', 'bio.t3.r2c2', 'bio.t3.r2c3', 'bio.t3.r2c4', 0],
  ['bio.t3.r3c1', 'bio.t3.r3c2', 'bio.t3.r3c3', 'bio.t3.r3c4', 0],
  ['bio.t3.r4c1', 'bio.t3.r4c2', 'bio.t3.r4c3', 'bio.t3.r4c4', 2],
  ['bio.t3.r5c1', 'bio.t3.r5c2', 'bio.t3.r5c3', 'bio.t3.r5c4', 0],
  ['bio.t3.r6c1', 'bio.t3.r6c2', 'bio.t3.r6c3', 'bio.t3.r6c4', 3],
  ['bio.t3.r7c1', 'bio.t3.r7c2', 'bio.t3.r7c3', 'bio.t3.r7c4', 0],
  ['bio.t3.r8c1', 'bio.t3.r8c2', 'bio.t3.r8c3', 'bio.t3.r8c4', 0],
  ['bio.t3.r9c1', 'bio.t3.r9c2', 'bio.t3.r9c3', 'bio.t3.r9c4', 1],
];
// 表4 三层结构
const t4Rows: [string, string, string][] = [
  ['bio.t4.r1c1', 'bio.t4.r1c2', 'bio.t4.r1c3'],
  ['bio.t4.r2c1', 'bio.t4.r2c2', 'bio.t4.r2c3'],
  ['bio.t4.r3c1', 'bio.t4.r3c2', 'bio.t4.r3c3'],
];
// 表5 BOM
const t5Rows = [1, 2, 3, 4, 5, 6, 7];

export default function HumanoidBionicShoePage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-4">{t('bio.hero.badge')}</Badge>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              {t('bio.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              {t('bio.hero.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {(['point1', 'point2', 'point3', 'point4'] as const).map((p) => (
                <Badge key={p} className="text-sm px-4 py-1.5">{t(`bio.hero.${p}`)}</Badge>
              ))}
            </div>
          </div>
          <div className="max-w-4xl mx-auto rounded-xl overflow-hidden border bg-card">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/bionic-shoe/standard-baseplate.jpg"
              alt={t('bio.s21.imgcap')}
              className="w-full h-56 md:h-72 object-cover"
            />
            <p className="text-sm text-muted-foreground p-4 text-center">{t('bio.s21.imgcap')}</p>
          </div>
        </div>
      </section>

      {/* Section 1: 传统方案短板 */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">{t('bio.s1.title')}</h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-10">{t('bio.s1.lead')}</p>
          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto mb-10">
            {(['p1', 'p2', 'p3', 'p4', 'p5', 'p6'] as const).map((k) => (
              <Card key={k}>
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <XCircleIcon />
                    <div>
                      <p className="font-semibold">{t(`bio.s1.${k}t`)}</p>
                      <p className="text-sm text-muted-foreground mt-1">{t(`bio.s1.${k}d`)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* 表1 痛点—影响对照 */}
          <Card className="max-w-5xl mx-auto">
            <CardHeader>
              <CardTitle className="text-lg">{t('bio.t1.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('bio.t1.h1')}</TableHead>
                    <TableHead>{t('bio.t1.h2')}</TableHead>
                    <TableHead>{t('bio.t1.h3')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {t1Rows.map((r) => (
                    <TableRow key={r}>
                      <TableCell className="font-medium">{t(`bio.t1.r${r}c1`)}</TableCell>
                      <TableCell>{t(`bio.t1.r${r}c2`)}</TableCell>
                      <TableCell className="text-muted-foreground">{t(`bio.t1.r${r}c3`)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section 2: 标准化模块化解决方案 */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">{t('bio.s2.title')}</h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-10">{t('bio.s2.lead')}</p>

          <div className="max-w-5xl mx-auto space-y-10">
            {/* 2.1 标准足底板 + 双密度复合鞋底 */}
            <div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4">{t('bio.s21.title')}</h3>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <p className="text-muted-foreground">{t('bio.s21.body')}</p>
                <div className="rounded-xl overflow-hidden border bg-card">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src="/bionic-shoe/standard-baseplate.jpg" alt={t('bio.s21.title')} className="w-full h-56 object-cover" />
                  <p className="text-sm text-muted-foreground p-3">{t('bio.s21.imgcap')}</p>
                </div>
              </div>
            </div>

            {/* 2.2 标准化固定孔结构与接口 */}
            <div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4">{t('bio.s22.title')}</h3>
              <div className="grid md:grid-cols-2 gap-5">
                {(['p1', 'p2'] as const).map((k) => (
                  <Card key={k}>
                    <CardContent className="pt-6">
                      <div className="flex gap-3">
                        <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                        <div>
                          <p className="font-semibold">{t(`bio.s22.${k}t`)}</p>
                          <p className="text-sm text-muted-foreground mt-1">{t(`bio.s22.${k}d`)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* 2.3 建议的设计改进 */}
            <div>
              <h3 className="text-xl md:text-2xl font-semibold mb-6">{t('bio.s23.title')}</h3>
              <div className="space-y-8">
                {/* p1 双密度 + 竖图 */}
                <div className="grid md:grid-cols-2 gap-6 items-start">
                  <ImprovementCard k="p1" />
                  <div className="rounded-xl overflow-hidden border bg-card">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/bionic-shoe/dual-density-sole.jpg" alt={t('bio.s23.img1cap')} className="w-full h-96 object-cover" />
                    <p className="text-sm text-muted-foreground p-3">{t('bio.s23.img1cap')}</p>
                  </div>
                </div>
                {/* p2-p3 仿生轮廓 + 翘角 */}
                <div className="grid md:grid-cols-2 gap-6 items-start">
                  <div className="space-y-5">
                    <ImprovementCard k="p2" />
                    <ImprovementCard k="p3" />
                  </div>
                  <div className="rounded-xl overflow-hidden border bg-card">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/bionic-shoe/toe-heel-spring.jpg" alt={t('bio.s23.img3cap')} className="w-full h-56 object-cover" />
                    <p className="text-sm text-muted-foreground p-3">{t('bio.s23.img3cap')}</p>
                  </div>
                </div>
                {/* p4 分区防滑 */}
                <div className="grid md:grid-cols-2 gap-6 items-start">
                  <ImprovementCard k="p4" />
                  <div className="rounded-xl overflow-hidden border bg-card">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/bionic-shoe/zoned-tread.jpg" alt={t('bio.s23.img4cap')} className="w-full h-72 object-cover" />
                    <p className="text-sm text-muted-foreground p-3">{t('bio.s23.img4cap')}</p>
                  </div>
                </div>
                {/* p5-p7 防撞/模块化/金属垫片 */}
                <div className="grid md:grid-cols-2 gap-6 items-start">
                  <div className="space-y-5">
                    <ImprovementCard k="p5" />
                    <ImprovementCard k="p6" />
                    <ImprovementCard k="p7" />
                  </div>
                  <div className="rounded-xl overflow-hidden border bg-card h-fit">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/bionic-shoe/metal-washer.jpg" alt={t('bio.s23.img6cap')} className="w-full h-56 object-cover" />
                    <p className="text-sm text-muted-foreground p-3">{t('bio.s23.img6cap')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 2.4 痛点—方案对应表 */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('bio.t2.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/3">{t('bio.t2.h1')}</TableHead>
                      <TableHead>{t('bio.t2.h2')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {t2Rows.map((r) => (
                      <TableRow key={r}>
                        <TableCell className="font-medium">{t(`bio.t2.r${r}c1`)}</TableCell>
                        <TableCell>{t(`bio.t2.r${r}c2`)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 3: 关键技术指标与三层复合结构 */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{t('bio.s3.title')}</h2>
          <div className="max-w-5xl mx-auto space-y-10">
            {/* 3.1 量化指标 */}
            <div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4">{t('bio.s31.title')}</h3>
              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('bio.t3.h1')}</TableHead>
                        <TableHead>{t('bio.t3.h2')}</TableHead>
                        <TableHead>{t('bio.t3.h3')}</TableHead>
                        <TableHead className="text-primary font-semibold">{t('bio.t3.h4')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {t3Rows.map(([fn, ind, intl, rs, span], i) => (
                        <TableRow key={i}>
                          {span > 0 && (
                            <TableCell rowSpan={span} className="font-medium align-top">
                              {t(fn)}
                            </TableCell>
                          )}
                          <TableCell>{t(ind)}</TableCell>
                          <TableCell className="text-muted-foreground">{t(intl)}</TableCell>
                          <TableCell className="font-semibold text-primary bg-primary/5">{t(rs)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card className="mt-4 border-primary/20 bg-primary/5">
                <CardContent className="flex gap-3 pt-6">
                  <Quote className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                  <p className="text-sm text-muted-foreground">{t('bio.s31.note')}</p>
                </CardContent>
              </Card>
            </div>

            {/* 3.2 三层复合结构 */}
            <div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4">{t('bio.s32.title')}</h3>
              <p className="text-muted-foreground mb-4">{t('bio.s32.lead')}</p>
              <div className="grid md:grid-cols-2 gap-6 items-start">
                <Card>
                  <CardContent className="pt-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('bio.t4.h1')}</TableHead>
                          <TableHead>{t('bio.t4.h2')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {t4Rows.map(([layer, mat, fn], i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <p className="font-medium">{t(layer)}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{t(mat)}</p>
                            </TableCell>
                            <TableCell className="text-sm">{t(fn)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <div className="rounded-xl overflow-hidden border bg-card">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src="/bionic-shoe/three-layer-structure.jpg" alt={t('bio.s32.imgcap')} className="w-full h-64 object-cover" />
                  <p className="text-sm text-muted-foreground p-3">{t('bio.s32.imgcap')}</p>
                </div>
              </div>
            </div>

            {/* 3.3 推荐总成 */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('bio.s33.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{t('bio.s33.features')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 4: 成本与交付 */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{t('bio.s4.title')}</h2>
          <div className="max-w-5xl mx-auto space-y-10">
            {/* 4.1 BOM */}
            <div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4">{t('bio.s41.title')}</h3>
              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('bio.t5.h1')}</TableHead>
                        <TableHead>{t('bio.t5.h2')}</TableHead>
                        <TableHead>{t('bio.t5.h3')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {t5Rows.map((r) => (
                        <TableRow key={r}>
                          <TableCell className="font-medium">{t(`bio.t5.r${r}c1`)}</TableCell>
                          <TableCell>{t(`bio.t5.r${r}c2`)}</TableCell>
                          <TableCell className="text-muted-foreground">{t(`bio.t5.r${r}c3`)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <p className="text-sm text-muted-foreground mt-3">{t('bio.s41.note')}</p>
            </div>

            {/* 4.2 交付流程 */}
            <div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4">{t('bio.s42.title')}</h3>
              <div className="grid md:grid-cols-5 gap-4">
                {(['step1', 'step2', 'step3', 'step4', 'step5'] as const).map((s, i) => (
                  <Card key={s}>
                    <CardContent className="pt-6">
                      <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold mb-3">
                        {i + 1}
                      </div>
                      <p className="text-sm text-muted-foreground">{t(`bio.s42.${s}`)}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: 演进路线 */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">{t('bio.s5.title')}</h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-10">{t('bio.s5.lead')}</p>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {(['p1', 'p2', 'p3'] as const).map((k) => (
              <Card key={k}>
                <CardHeader>
                  <CardTitle className="text-base text-primary">{t(`bio.s5.${k}t`)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{t(`bio.s5.${k}d`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{t('bio.cta.title')}</h2>
            <p className="text-muted-foreground mb-8">{t('bio.cta.desc')}</p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t('bio.cta.button')}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function ImprovementCard({ k }: { k: 'p1' | 'p2' | 'p3' | 'p4' | 'p5' | 'p6' | 'p7' }) {
  const { t } = useLanguage();
  return (
    <Card className="h-full">
      <CardContent className="pt-6">
        <div className="flex gap-3">
          <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
          <div>
            <p className="font-semibold">{t(`bio.s23.${k}t`)}</p>
            <p className="text-sm text-muted-foreground mt-1">{t(`bio.s23.${k}d`)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function XCircleIcon() {
  return (
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-destructive/10">
      <span className="h-2 w-2 rounded-full bg-destructive" />
    </span>
  );
}
